import { Badge } from "@design";
import { Telescope } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { DocumentLineageResponse, DocumentVersionResponse, SrcSchemasChatMessageResponse } from "@/api";
import { MessageAuthor } from "@/components/chat/MessageAuthor";
import { MessageBubble } from "@/components/chat/MessageBubble";
import { formatDate } from "@/i18n/format";
import { initialsOf } from "@/lib/initials";
import { AssistantMessage } from "./AssistantMessage";
import { DocumentCard } from "./documents/DocumentCard";
import type { OpenDocument } from "./documents/documentInfo";
import { DraftCards } from "./drafts/DraftCards";
import type { SavedDraft } from "./drafts/draftModel";
import { MessageAttachments } from "./MessageAttachments";
import { numberedSources, parseSources } from "./messageSources";
import { ResearchProposalCard } from "./research/ResearchProposalCard";
import { ResearchFailure, ResearchSummary } from "./research/ResearchSummary";
import { proposalFromMetadata, researchFailure, researchFromMetadata } from "./research/researchModel";

const TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

/**
 * Saved messages are the source of truth. Tool calls/results and empty
 * assistant steps are internal to a reply, so only user turns and assistant
 * answers with content are shown, plus a reply carrying a research proposal,
 * which may have no text of its own.
 */
export function visibleMessages(messages: readonly SrcSchemasChatMessageResponse[]) {
  return messages.filter(
    (m) =>
      m.role === "user" ||
      (m.role === "assistant" && (m.content.trim().length > 0 || proposalFromMetadata(m.messageMetadata) !== null)),
  );
}

export function SavedMessage({
  message,
  myUuid,
  agentName,
  documents,
  onOpenDocument,
  drafts,
}: Readonly<{
  message: SrcSchemasChatMessageResponse;
  myUuid?: string;
  agentName?: string | null;
  /** Documents this reply created or revised. */
  documents?: { doc: DocumentLineageResponse; version: DocumentVersionResponse }[];
  onOpenDocument?: (open: OpenDocument) => void;
  /** Build with AI drafts this reply proposed. */
  drafts?: SavedDraft[];
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "message" });
  const time = formatDate(message.createdAt, TIME);
  if (message.role === "assistant") {
    const proposal = proposalFromMetadata(message.messageMetadata);
    const research = researchFromMetadata(message.messageMetadata);
    // A failed research run is explained from its code, not by the backend's saved text.
    const failure = researchFailure(message.messageMetadata);
    return (
      <AssistantMessage
        name={agentName}
        meta={time}
        text={failure ? "" : message.content}
        sources={parseSources(message.messageMetadata?.sources)}
        citations={numberedSources(message.messageMetadata?.sources)}
        activity={failure ? <ResearchFailure code={failure} /> : research && <ResearchSummary research={research} />}
        footer={
          <>
            {proposal && <ResearchProposalCard proposal={proposal} />}
            {isLegacyResearch(message.messageMetadata) && (
              <div>
                <Badge variant="neutral" icon={<Telescope size={12} />}>
                  {t("importedFromDeepResearch")}
                </Badge>
              </div>
            )}
            {documents?.length && onOpenDocument ? (
              <div className="space-y-2">
                {documents.map(({ doc, version }) => (
                  <DocumentCard
                    key={version.attachmentUuid}
                    doc={doc}
                    version={version}
                    onOpen={() => onOpenDocument({ lineageUuid: doc.uuid })}
                  />
                ))}
              </div>
            ) : null}
            {drafts?.length ? <DraftCards drafts={drafts} /> : null}
          </>
        }
      />
    );
  }
  // Shared chats: another person's turn reads as a participant, not as "You".
  if (message.senderUuid && myUuid && message.senderUuid !== myUuid) {
    const name = message.senderName ?? "";
    return (
      <div>
        <MessageAuthor kind="participant" name={name} initials={initialsOf(name)} meta={time} />
        <div className="mt-1">
          <MessageBubble variant="participant">{message.content}</MessageBubble>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-end gap-1.5">
      <MessageBubble variant="user" author={t("you")} time={time}>
        {message.content}
      </MessageBubble>
      <MessageAttachments message={message} />
    </div>
  );
}

/** A v1 Deep Research report copied into the chat (MHS-790); v2 research replies have no `legacy`. */
function isLegacyResearch(metadata: SrcSchemasChatMessageResponse["messageMetadata"]) {
  const research = metadata?.research;
  return typeof research === "object" && research !== null && "legacy" in research && research.legacy === true;
}

/** Centered column the thread content sits in. */
export function ThreadColumn({ children }: Readonly<{ children: ReactNode }>) {
  return <div className="mx-auto w-full max-w-[720px] space-y-6 px-4 py-8 md:px-6">{children}</div>;
}
